import React, { useEffect, useState } from 'react';
import { getFunnelStats, getRetentionStats, getProductPerformance, FunnelStats, RetentionStats, ProductPerformance } from '../../api/growth';
import { TrendingUp, Users, Target, Lightbulb, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';

interface GrowthTabProps {
    sellers?: { id: string; username: string }[];
}

export const GrowthTab: React.FC<GrowthTabProps> = ({ sellers = [] }) => {
    const [selectedSellerId, setSelectedSellerId] = useState<string>('all');
    const [funnel, setFunnel] = useState<FunnelStats | null>(null);
    const [retention, setRetention] = useState<RetentionStats | null>(null);
    const [performance, setPerformance] = useState<ProductPerformance[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const sId = selectedSellerId === 'all' ? undefined : selectedSellerId;
                const [funnelRes, retentionRes, perfRes] = await Promise.all([
                    getFunnelStats(sId),
                    getRetentionStats(sId),
                    getProductPerformance(sId)
                ]);
                setFunnel(funnelRes);
                setRetention(retentionRes);
                setPerformance(perfRes);
            } catch (error) {
                console.error('Failed to fetch growth data', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedSellerId]);

    const funnelItems = [
        { label: '조회수', value: funnel?.views || 0, color: 'bg-blue-500' },
        { label: '찜하기', value: funnel?.likes || 0, color: 'bg-pink-500' },
        { label: '주문수', value: funnel?.orders || 0, color: 'bg-emerald-500' }
    ];

    const totalInteractions = funnelItems[0].value || 1;

    return (
        <div className="space-y-6 pb-12">
            {/* Seller Select Filter */}
            {sellers.length > 0 && (
                <div className="flex items-center gap-2 px-1">
                    <div className="flex-1 relative">
                        <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <select
                            value={selectedSellerId}
                            onChange={(e) => setSelectedSellerId(e.target.value)}
                            className="w-full h-10 pl-9 pr-4 bg-white border border-gray-200 rounded-2xl text-[11px] font-black outline-none focus:ring-2 ring-indigo-500/20 focus:border-indigo-500 transition-all appearance-none"
                        >
                            <option value="all">전체 판매자 성장 분석</option>
                            {sellers.map(s => (
                                <option key={s.id} value={s.id}>{s.username}의 성과 보기</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="py-20 text-center space-y-4">
                    <div className="flex justify-center">
                        <TrendingUp size={48} className="text-gray-200 animate-pulse" />
                    </div>
                    <p className="text-sm font-black text-gray-400">성장 데이터 분석 중...</p>
                </div>
            ) : (
                <>
                    {/* Actionable Insights */}
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-6 rounded-[32px] text-white shadow-xl">
                        <div className="flex items-center gap-2 mb-4">
                            <Lightbulb size={24} className="text-yellow-300" />
                            <h3 className="text-lg font-black">AI 성장 인사이트</h3>
                        </div>
                        <div className="space-y-3">
                            {performance.filter(p => (p.category || '').includes('Zombie')).slice(0, 2).map(p => (
                                <div key={p.id} className="bg-white/10 backdrop-blur-md p-4 rounded-2xl flex gap-3 items-start border border-white/20">
                                    <AlertCircle size={20} className="shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-bold">"{p.name}" 상품의 전환율이 낮습니다.</p>
                                        <p className="text-xs opacity-80 mt-1">조회수는 높지만 주문이 없습니다. 상세 설명을 보강하거나 소폭 할인을 고려해보세요.</p>
                                    </div>
                                </div>
                            ))}
                            {retention && retention.returningCustomers > 5 && (
                                <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl flex gap-3 items-start border border-white/20">
                                    <CheckCircle size={20} className="shrink-0 mt-0.5 text-emerald-300" />
                                    <div>
                                        <p className="text-sm font-bold">단골 고객 비중이 높습니다!</p>
                                        <p className="text-xs opacity-80 mt-1">재구매 고객을 위한 별도의 혜택(쿠폰, 사은품)을 홍보하여 충성도를 높여보세요.</p>
                                    </div>
                                </div>
                            )}
                            {performance.length === 0 && (
                                <p className="text-xs opacity-60 text-center py-4 italic">데이터가 충분하지 않아 인사이트를 생성할 수 없습니다.</p>
                            )}
                        </div>
                    </div>

                    {/* Main Funnel Analysis */}
                    <div className="bg-white p-6 rounded-[32px] shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-6">
                            <Target size={20} className="text-gray-400" />
                            <h3 className="text-lg font-black text-gray-900">구매 퍼널 분석</h3>
                        </div>
                        <div className="space-y-6">
                            {funnelItems.map((item, idx) => {
                                const width = (item.value / totalInteractions) * 100;
                                return (
                                    <div key={item.label} className="space-y-2">
                                        <div className="flex justify-between items-baseline">
                                            <span className="text-xs font-black text-gray-500">{item.label}</span>
                                            <span className="text-sm font-black text-gray-900">{item.value.toLocaleString()}</span>
                                        </div>
                                        <div className="h-3 w-full bg-gray-50 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full ${item.color} transition-all duration-1000`}
                                                style={{ width: `${Math.max(width, 2)}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-8 pt-6 border-t border-gray-50 flex justify-around">
                            <div className="text-center">
                                <p className="text-[10px] font-bold text-gray-400 mb-1">전체 전환율</p>
                                <p className="text-xl font-black text-gray-900">
                                    {funnel?.views ? ((funnel.orders / funnel.views) * 100).toFixed(1) : 0}%
                                </p>
                            </div>
                            <div className="text-center">
                                <p className="text-[10px] font-bold text-gray-400 mb-1">관심 상품률</p>
                                <p className="text-xl font-black text-gray-900">
                                    {funnel?.views ? ((funnel.likes / funnel.views) * 100).toFixed(1) : 0}%
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {/* Retention Card */}
                        <div className="bg-white p-5 rounded-[32px] shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-4">
                                <Users size={16} className="text-gray-400" />
                                <h3 className="text-sm font-black text-gray-900">고객 유지율</h3>
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-end gap-2">
                                    <p className="text-3xl font-black text-gray-900">
                                        {retention ? (
                                            (retention.returningCustomers / (retention.newCustomers + retention.returningCustomers || 1) * 100).toFixed(0)
                                        ) : 0}%
                                    </p>
                                    <span className="text-[10px] font-bold text-emerald-500 mb-1 flex items-center">
                                        재구매율 <TrendingUp size={10} className="ml-0.5" />
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-[10px] font-bold">
                                        <span className="text-gray-400">신규</span>
                                        <span className="text-gray-900">{retention?.newCustomers}명</span>
                                    </div>
                                    <div className="flex justify-between text-[10px] font-bold">
                                        <span className="text-gray-400">재구매</span>
                                        <span className="text-gray-900">{retention?.returningCustomers}명</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Top Product Category */}
                        <div className="bg-white p-5 rounded-[32px] shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-4">
                                <TrendingUp size={16} className="text-gray-400" />
                                <h3 className="text-sm font-black text-gray-900">주력 상품군</h3>
                            </div>
                            <div className="space-y-3">
                                {performance.slice(0, 3).map((p, i) => (
                                    <div key={p.id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${i === 0 ? 'bg-indigo-500' : 'bg-gray-200'}`} />
                                            <span className="text-[11px] font-bold text-gray-600 truncate">{p.name}</span>
                                        </div>
                                        <span className="text-[11px] font-black text-gray-900">{p.sales}건</span>
                                    </div>
                                ))}
                                {performance.length === 0 && <p className="text-[10px] text-gray-400">데이터 없음</p>}
                            </div>
                        </div>
                    </div>

                    {/* Product List Ranking */}
                    {performance.length > 0 && (
                        <div className="bg-white p-6 rounded-[32px] shadow-sm border border-gray-100">
                            <h3 className="text-lg font-black text-gray-900 mb-6">상품별 성과 상세</h3>
                            <div className="space-y-4">
                                {performance.map(p => (
                                    <div key={p.id} className="flex justify-between items-center py-3 border-b last:border-0 border-gray-50">
                                        <div className="min-w-0 flex-1 pr-4">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <h4 className="text-sm font-black text-gray-900 truncate">{p.name}</h4>
                                                <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase ${(p.category || '').includes('Hero') ? 'bg-indigo-100 text-indigo-600' :
                                                    (p.category || '').includes('Zombie') ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'
                                                    }`}>
                                                    {p.category}
                                                </span>
                                            </div>
                                            <div className="flex gap-3 text-[10px] font-bold text-gray-400">
                                                <span>조회 {p.views}</span>
                                                <span>찜 {p.likes || 0}</span>
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-sm font-black text-gray-900">{p.sales}건 주문</div>
                                            <div className="text-[10px] font-bold text-emerald-500">
                                                자세히 <ArrowRight size={10} className="inline ml-0.5" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
